import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Backdrop,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemSecondaryAction,
  Fade,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  PersonOutline,
  ExitToApp,
  AdminPanelSettings,
  Warning,
  KeyboardArrowDown,
  Person,
  SupervisorAccount,
  People,
  Search,
  Close
} from '@mui/icons-material';
import { AuthContext } from '../context/AuthContext';
import axiosInstance from './axiosInstance';

const ImpersonationBar = () => {
  const { userRole, isAuthenticated, login } = useContext(AuthContext);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [realAdmin, setRealAdmin] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [transitioning, setTransitioning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // Check if we're currently impersonating
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.isImpersonating) {
          setIsImpersonating(true);
          setRealAdmin({
            id: payload.adminId,
            email: payload.adminEmail
          });
          setCurrentUser({
            id: payload.id,
            email: payload.email,
            role: payload.role,
            name: payload.employeeName || payload.email
          });
        }
      } catch (e) {
        console.error('Error parsing token:', e);
      }
    }
  }, []);

  useEffect(() => {
    // Listen for custom event from Nav to open admin tools
    const handleOpenAdminTools = (event) => {
      setModalOpen(true);
      loadUsers();
    };

    window.addEventListener('openAdminTools', handleOpenAdminTools);
    return () => {
      window.removeEventListener('openAdminTools', handleOpenAdminTools);
    };
  }, []);

  useEffect(() => {
    // Load users if admin and not impersonating
    if (userRole === 'admin' && !isImpersonating && isAuthenticated) {
      loadUsers();
    }
  }, [userRole, isImpersonating, isAuthenticated]);

  const loadUsers = async () => {
    try {
      const response = await axiosInstance.get('/admin/impersonate/users');
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users');
    }
  };

  const handleClose = () => {
    setModalOpen(false);
    setSelectedUserId('');
    setSearchQuery('');
    setError('');
  };

  const startImpersonation = async (userId) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axiosInstance.post('/admin/impersonate/start', {
        targetUserId: userId
      });
      
      // Update auth context with new token
      const { token, impersonating, realUser } = response.data;
      localStorage.setItem('token', token);
      // Store userDetails reflecting the impersonated identity so UI gates work pre-reload
      try {
        const details = {
          email: impersonating.email,
          role: impersonating.role,
          employeeId: impersonating.id, // may represent user id; fallback until employeeId resolved
          position: impersonating.position || null,
          locationName: impersonating.location || null,
          isImpersonating: true
        };
        localStorage.setItem('userDetails', JSON.stringify(details));
      } catch (e) {
        // no-op
      }
      
      // Set impersonation state
      setIsImpersonating(true);
      setRealAdmin(realUser);
      setCurrentUser({
        ...impersonating,
        name: impersonating.name || impersonating.email
      });
      
      handleClose();
      setTransitioning(true);
      
      // Add a visual transition effect
      document.body.style.transition = 'opacity 0.3s ease-out';
      document.body.style.opacity = '0.3';
      
      setTimeout(() => {
        // Redirect based on the impersonated user's role
        const targetRole = impersonating.role;
        if (targetRole === 'user') {
          // Regular users go to employee forms
          window.location.href = '/employee_forms';
        } else if (targetRole === 'manager') {
          // Managers go to dashboard
          window.location.href = '/dashboard';
        } else {
          // Admins stay on current page
          window.location.reload();
        }
      }, 300);
      
    } catch (error) {
      console.error('Error starting impersonation:', error);
      setError(error.response?.data?.error || 'Failed to start impersonation');
    } finally {
      setLoading(false);
    }
  };

  const stopImpersonation = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axiosInstance.post('/admin/impersonate/stop');
      
      // Update auth context with admin token
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      // Clear impersonation-flavored userDetails so admin context is recalculated
      try {
        const details = {
          email: user.email,
          role: user.role,
          employeeId: user.id,
          isImpersonating: false
        };
        localStorage.setItem('userDetails', JSON.stringify(details));
      } catch (e) {
        // no-op
      }
      
      // Clear impersonation state
      setIsImpersonating(false);
      setRealAdmin(null);
      setCurrentUser(null);
      
      // Reload the page to apply admin permissions
      window.location.reload();
      
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      setError(error.response?.data?.error || 'Failed to stop impersonation');
    } finally {
      setLoading(false);
    }
  };

  // Only show for admins or when impersonating
  if (!isAuthenticated || (userRole !== 'admin' && !isImpersonating)) {
    return null;
  }

  // Group users by role
  const groupedUsers = users.reduce((acc, user) => {
    const role = user.role || 'user';
    if (!acc[role]) acc[role] = [];
    acc[role].push(user);
    return acc;
  }, {});

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <AdminPanelSettings fontSize="small" />;
      case 'manager':
        return <SupervisorAccount fontSize="small" />;
      default:
        return <Person fontSize="small" />;
    }
  };

  const getRoleDescription = (role) => {
    switch (role) {
      case 'admin':
        return 'Full access to all features and locations';
      case 'manager':
        return 'Can view and manage employees in their location only';
      case 'user':
      default:
        return 'Can view and edit their own information and forms';
    }
  };

  return (
    <>
      {/* Loading backdrop during transition */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: 9999,
          flexDirection: 'column',
          gap: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.8)'
        }}
        open={transitioning}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h5" align="center">
          Switching view...
        </Typography>
        <Typography variant="body1" align="center" sx={{ maxWidth: 400 }}>
          Loading {currentUser?.role || 'user'} permissions and data access
        </Typography>
      </Backdrop>

      {isImpersonating ? (
        <Box
          sx={{
            position: 'fixed',
            top: 64, // Height of nav bar
            left: 0,
            right: 0,
            zIndex: 1199, // Just below nav bar
            background: 'linear-gradient(90deg, #d32f2f 0%, #c62828 100%)',
            borderBottom: '2px solid #b71c1c',
          }}
        >
          <Container maxWidth={false} sx={{ height: '100%' }}>
            <Box
              sx={{
                color: 'white',
                padding: { xs: '8px 16px', sm: '4px 24px' },
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 2 },
                minHeight: { xs: '56px', sm: '32px' },
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
              }}
            >
              <Warning sx={{ fontSize: 16, flexShrink: 0 }} />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, sm: 2 },
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Box sx={{ display: { xs: 'block', sm: 'none' }, width: '100%' }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '0.75rem',
                      lineHeight: 1.2,
                      mb: 0.5
                    }}
                  >
                    <strong>Viewing as:</strong> {currentUser?.name || currentUser?.email}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '0.75rem',
                      lineHeight: 1.2
                    }}
                  >
                    <strong>Role:</strong> {currentUser?.role?.charAt(0).toUpperCase() + currentUser?.role?.slice(1)}
                  </Typography>
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap',
                    display: { xs: 'none', sm: 'block' }
                  }}
                >
                  <strong>Viewing as:</strong> {currentUser?.name || currentUser?.email}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    display: { xs: 'none', sm: 'block' }
                  }}
                >
                  <strong>•</strong>
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    whiteSpace: { xs: 'normal', sm: 'nowrap' },
                    display: { xs: 'none', sm: 'block' }
                  }}
                >
                  <strong>Role:</strong> {currentUser?.role?.charAt(0).toUpperCase() + currentUser?.role?.slice(1)}
                  {currentUser?.position && ` (${currentUser.position})`}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    display: { xs: 'none', md: 'block' }
                  }}
                >
                  <strong>•</strong>
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    display: { xs: 'none', md: 'block' },
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getRoleDescription(currentUser?.role)}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={stopImpersonation}
                disabled={loading}
                sx={{ 
                  borderColor: 'white',
                  color: 'white',
                  padding: { xs: '2px 8px', sm: '2px 12px' },
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  minHeight: '24px',
                  flexShrink: 0,
                  position: { xs: 'absolute', sm: 'relative' },
                  right: { xs: 16, sm: 'auto' },
                  top: { xs: '50%', sm: 'auto' },
                  transform: { xs: 'translateY(-50%)', sm: 'none' },
                  '&:hover': { 
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)' 
                  }
                }}
              >
                {isMobile ? 'Exit' : 'Exit Impersonation'}
              </Button>
            </Box>
          </Container>
        </Box>
      ) : null}

      {/* Admin Tools Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#276a78',
            color: 'white',
            py: 2,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminPanelSettings />
            <Typography variant="h6">Select User to Impersonate</Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Search Bar */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              fullWidth
              placeholder="Search by name, email, or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ m: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading users...
              </Typography>
            </Box>
          ) : (
            <List sx={{ py: 0 }}>
              {Object.entries(groupedUsers).map(([role, roleUsers]) => {
                // Filter users based on search query
                const filteredUsers = roleUsers.filter(user => {
                  const searchLower = searchQuery.toLowerCase();
                  return (
                    (user.name && user.name.toLowerCase().includes(searchLower)) ||
                    (user.email && user.email.toLowerCase().includes(searchLower)) ||
                    (user.location && user.location.toLowerCase().includes(searchLower)) ||
                    (user.position && user.position.toLowerCase().includes(searchLower))
                  );
                });

                if (filteredUsers.length === 0) return null;

                return (
                  <Box key={role}>
                    <Box
                      sx={{
                        px: 3,
                        py: 1,
                        bgcolor: 'grey.100',
                        borderBottom: 1,
                        borderColor: 'divider',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        {getRoleIcon(role)}
                        {role}s ({filteredUsers.length})
                      </Typography>
                    </Box>
                    {filteredUsers.map((user) => (
                      <ListItem
                        key={user.id}
                        button
                        onClick={() => startImpersonation(user.id)}
                        sx={{
                          py: 2,
                          px: 3,
                          alignItems: 'flex-start',
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                        secondaryAction={
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              startImpersonation(user.id);
                            }}
                            sx={{
                              textTransform: 'none',
                              borderColor: '#276a78',
                              color: '#276a78',
                              whiteSpace: 'nowrap',
                              '&:hover': {
                                borderColor: '#1a4a52',
                                bgcolor: 'rgba(39, 106, 120, 0.04)'
                              }
                            }}
                          >
                            View as User
                          </Button>
                        }
                      >
                        <ListItemAvatar sx={{ mt: 0.5 }}>
                          <Avatar sx={{ bgcolor: '#276a78' }}>
                            {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body1" fontWeight="medium">
                              {user.name || user.email}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                  display: 'block',
                                  mb: 0.5,
                                  pr: 12, // Add padding to prevent overlap with button
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                              >
                                {user.email}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {user.position && `${user.position} • `}
                                {user.location || 'No location'}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </Box>
                );
              })}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImpersonationBar;
